import * as React from 'react';
import type { PublicPartner } from '@proptr/shared';
import { client } from '../main.js';
import { cn } from '../lib/utils.js';

// Partner buttons the brokerage can switch on — configured by the
// proptr.ee team in the admin app (name, colour, logo, form), so the only
// edit here is on/off. Shared by the agency, agents and affiliate
// settings pages; each owns its own `{ [partnerKey]: boolean }` map.

let cache: Promise<PublicPartner[]> | null = null;
function loadPartners(): Promise<PublicPartner[]> {
  // One fetch per page load — every settings page shows the same list.
  cache ??= client.partners
    .listAvailable()
    .then((r) => r.items)
    .catch((err) => {
      cache = null;
      throw err;
    });
  return cache;
}

export function PartnerToggleList({
  value,
  onChange,
}: {
  value: Record<string, boolean>;
  onChange: (next: Record<string, boolean>) => void;
}) {
  const [partners, setPartners] = React.useState<PublicPartner[] | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    let alive = true;
    loadPartners()
      .then((items) => alive && setPartners(items))
      .catch((err: Error) => alive && setError(err.message));
    return () => {
      alive = false;
    };
  }, []);

  if (error) {
    return (
      <div className="mt-1 rounded-md border border-destructive/30 bg-destructive/10 p-3 font-mono text-[11px] text-destructive">
        {error}
      </div>
    );
  }
  if (!partners) {
    return (
      <div className="mt-1 rounded-md border border-border bg-card/40 p-4 font-mono text-[11px] text-muted-foreground dark:border-white/10">
        Loading partners…
      </div>
    );
  }
  if (partners.length === 0) {
    return (
      <div className="mt-1 rounded-md border border-dashed border-border bg-card/40 p-6 text-center font-sans text-[12px] text-muted-foreground">
        No partner buttons are available yet.
      </div>
    );
  }
  return (
    <div className="mt-1 space-y-2">
      {partners.map((p) => (
        <PartnerToggleRow
          key={p.key}
          partner={p}
          enabled={value[p.key] === true}
          onChange={(v) => onChange({ ...value, [p.key]: v })}
        />
      ))}
    </div>
  );
}

function PartnerToggleRow({
  partner: p,
  enabled,
  onChange,
}: {
  partner: PublicPartner;
  enabled: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <div className="flex items-center gap-3 rounded-md border border-border bg-muted/30 p-3 dark:border-white/10">
      <span
        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full"
        style={{ backgroundColor: p.color }}
      >
        <span className="flex h-6 w-6 items-center justify-center overflow-hidden rounded-full bg-white">
          {p.logo ? (
            <img src={p.logo} alt="" className="h-4 w-4 object-contain" />
          ) : (
            <span className="text-[11px] font-bold" style={{ color: p.color }}>
              {p.name.charAt(0)}
            </span>
          )}
        </span>
      </span>
      <div className="min-w-0 flex-1">
        <div className="truncate font-sans text-[13px] font-semibold text-foreground">
          {p.name}
        </div>
        {p.subtitle && (
          <div className="truncate font-sans text-[12px] text-muted-foreground">
            {p.subtitle}
          </div>
        )}
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={enabled}
        aria-label={`${p.name} enabled`}
        onClick={() => onChange(!enabled)}
        className={cn(
          'relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background',
          enabled ? 'bg-primary' : 'bg-muted-foreground/30',
        )}
      >
        <span
          className={cn(
            'inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform',
            enabled ? 'translate-x-[18px]' : 'translate-x-[2px]',
          )}
        />
      </button>
    </div>
  );
}
